import React, { useRef } from "react"
import { useHistory } from "react-router-dom"
import { FormHandles } from "@unform/core"
import { Form } from "@unform/web"
import * as Yup from "yup"

import { useTranslation, useValidation } from "hooks"
import { translations } from "./translations"

import * as S from "./styles"
import { MainContainer } from "components/shared"
import { Input, Select, InputMask } from "components/shared/Form"

import { useMutation, useQuery } from "react-query"
import { apiGeneral, notify } from "services"

export const NewCommercialTeams: React.FC = () => {
  const formRef = useRef<FormHandles>(null)
  const { handleFormErrors } = useValidation()
  const { getTranslation } = useTranslation(translations)

  const schemaValidation = Yup.object().shape({
    erp_code: Yup.string().required(getTranslation("erpCodeIsRequired")),
    name: Yup.string().required(getTranslation("nameIsRequired")),
    phone_number: Yup.string().required(
      getTranslation("phoneNumberIsRequired")
    ),
    commercial_role_id: Yup.string().required(
      getTranslation("commercialRoleIsRequired")
    ),
    tracking_region_id: Yup.string().required(
      getTranslation("trackingRegionIsRequired")
    ),
  })

  const { mutate, isLoading: isPending } = useMutation({
    mutationKey: ["new-commercial-team"],
    mutationFn: async (data: any) => {
      await apiGeneral.post("/comercial-team", data)
    },
  })

  const { data: trackingRegions, isLoading: isLoadingTrackingRegions } =
    useQuery({
      queryKey: ["get-tracking-regions"],
      queryFn: async () => {
        const { data } = await apiGeneral.get("/tracking-region")
        return data
      },
      select: (data) => {
        return data.map((d: any) => ({ label: d.name, value: d.id }))
      },
    })

  const { data: commercialRoles, isLoading: isLoadingCommercialRoles } =
    useQuery({
      queryKey: ["get-commercial-roles"],
      queryFn: async () => {
        const { data } = await apiGeneral.get("/comercial-role")
        return data
      },
      select: (data) => {
        return data.map((d: any) => ({ label: d.name, value: d.id }))
      },
    })

  const history = useHistory()

  const handleSubmit = async (data: any) => {
    try {
      await schemaValidation.validate(data, {
        abortEarly: false,
      })

      mutate(data, {
        onSuccess: () => {
          notify(
            "success",
            getTranslation("newCommercialTeamRegisteredSuccessfully")
          )
          history.push("/settings/commercial-teams")
        },
        onError: () => {
          notify("error", getTranslation("errorRegisteringCommercialTeam"))
        },
      })
    } catch (error) {
      handleFormErrors(error, formRef)
    }
  }

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation("configuracoes")}{" "}
          <span>{getTranslation("novoCommercialTeam")}</span>
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini btStyle="dark" onClick={() => history.goBack()}>
            <S.IconArrowLeft />
            {getTranslation("voltar")}
          </S.ButtonMini>
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          placeholder=""
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <S.BoxContainer>
            <S.FormRow>
              <Input name="erp_code" label={getTranslation("codigoSAP")} />
              <Input name="name" label={getTranslation("nomeFantasia")} />
              <InputMask
                name="phone_number"
                label={getTranslation("telefone")}
                mask="(99) 9999-99999"
              />
            </S.FormRow>
            <S.FormRow>
              <Select
                name="commercial_role_id"
                label={getTranslation("commercialRole")}
                isLoading={isLoadingCommercialRoles}
                isDisabled={isLoadingCommercialRoles}
                options={commercialRoles ?? []}
                placeholder={getTranslation("selecione")}
              />
              <Select
                name="tracking_region_id"
                label={getTranslation("trackingRegion")}
                isLoading={isLoadingTrackingRegions}
                isDisabled={isLoadingTrackingRegions}
                options={trackingRegions ?? []}
                placeholder={getTranslation("selecione")}
              />
            </S.FormRow>
          </S.BoxContainer>
          <S.FormFooter>
            <S.FormRow>
              <S.Button
                btStyle="cancel"
                type="button"
                onClick={() => history.goBack()}
              >
                {getTranslation("cancelar")}
              </S.Button>
              <S.Button type="submit">
                {isPending ? <S.Loading /> : getTranslation("cadastrar")}
              </S.Button>
            </S.FormRow>
          </S.FormFooter>
        </Form>
      </S.PageContent>
    </MainContainer>
  )
}
